import { apiClient, getJson } from '@/lib/apiClient';
import type {
  Connector,
  Dataset,
  NewDatabaseConnector,
} from '@/features/connectors/types';

interface Wrapped<T> {
  data: T;
}

export const connectorsApi = {
  list: (): Promise<Connector[]> =>
    getJson<Wrapped<Connector[]>>('/connectors').then((r) => r.data),

  get: (id: string): Promise<Connector> =>
    getJson<Wrapped<Connector>>(`/connectors/${id}`).then((r) => r.data),

  createDatabase: async (input: NewDatabaseConnector): Promise<Connector> => {
    const res = await apiClient.post<Wrapped<Connector>>('/connectors', {
      name: input.name,
      type: 'database',
      config: {
        driver: input.driver,
        host: input.host,
        port: input.port,
        database: input.database,
        username: input.username,
        password: input.password,
      },
    });
    return res.data.data;
  },

  uploadFile: async (name: string, file: File, sheet?: string): Promise<Connector> => {
    const form = new FormData();
    form.append('name', name);
    form.append('file', file);
    if (sheet) {
      form.append('sheet', sheet);
    }
    const res = await apiClient.post<Wrapped<Connector>>('/connectors/file', form);
    return res.data.data;
  },

  test: async (id: string): Promise<Connector> => {
    const res = await apiClient.post<Wrapped<Connector>>(`/connectors/${id}/test`);
    return res.data.data;
  },

  sync: async (id: string): Promise<Connector> => {
    const res = await apiClient.post<Wrapped<Connector>>(`/connectors/${id}/sync`);
    return res.data.data;
  },

  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/connectors/${id}`);
  },

  datasets: (connectorId: string): Promise<Dataset[]> =>
    getJson<Wrapped<Dataset[]>>(`/connectors/${connectorId}/datasets`).then((r) => r.data),

  dataset: (id: string): Promise<Dataset> =>
    getJson<Wrapped<Dataset>>(`/datasets/${id}`).then((r) => r.data),

  preview: (id: string, limit = 50): Promise<Record<string, unknown>[]> =>
    getJson<Wrapped<Record<string, unknown>[]>>(`/datasets/${id}/preview`, { limit }).then(
      (r) => r.data,
    ),
};

export const connectorKeys = {
  all: ['connectors'] as const,
  detail: (id: string) => ['connectors', id] as const,
  datasets: (connectorId: string) => ['connectors', connectorId, 'datasets'] as const,
  dataset: (id: string) => ['datasets', id] as const,
  preview: (id: string) => ['datasets', id, 'preview'] as const,
};
